import React, { useState, useEffect } from "react";
import { Col, Card, CardBody, Form, Input } from "reactstrap";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDevicesRequest,
  updateDeviceRequest,
  searchDevicesRequest,
} from "../../store/DevicesPaymentStore/devicesActions";
import PageSection from '../../components/Loader/loader';

const AllDeviceInfo = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [editableRow, setEditableRow] = useState(null);
  const [editedDeviceName, setEditedDeviceName] = useState("");
  const [editedDeviceOS, setEditedDeviceOS] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;
  const dispatch = useDispatch();

  const { devicesData, searchResults = [], loading } = useSelector((state) => state.devices);

  const [displayedItems, setDisplayedItems] = useState([]);
  const [noResultsFound, setNoResultsFound] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (!devicesData || devicesData.length === 0) {
      dispatch(fetchDevicesRequest(1, 120));
    } else {
      setDisplayedItems(devicesData);
    }
  }, [dispatch, devicesData]);

  useEffect(() => {
    const handleSearch = () => {
      if (searchTerm.length >= 3) {
        setSearchLoading(true);
        dispatch(searchDevicesRequest(searchTerm));
      } else if (searchTerm.length === 0) {
        setDisplayedItems(devicesData);
        setNoResultsFound(false);
        setSearchLoading(false);
      }
    };

    const timeoutId = setTimeout(handleSearch, 500);

    return () => {
      clearTimeout(timeoutId);
      setSearchLoading(false);
    };
  }, [searchTerm, dispatch, devicesData]);

  useEffect(() => {
    if (searchTerm.length >= 3) {
      setSearchLoading(false);
      if (searchResults.length > 0) {
        setDisplayedItems(searchResults);
        setNoResultsFound(false);
      } else {
        setNoResultsFound(true);
        const clearItemsTimeout = setTimeout(() => {
          setDisplayedItems([]);
        }, 500);

        return () => clearTimeout(clearItemsTimeout);
      }
    } else {
      setDisplayedItems(devicesData);
      setNoResultsFound(false);
    }

  }, [searchResults, searchTerm, devicesData]);


  useEffect(() => {
    if (currentPage % 4 === 0 && devicesData.length <= currentPage * itemsPerPage) {
      const nextPage = currentPage / 4 + 1;
      dispatch(fetchDevicesRequest(nextPage, 120));
    }
  }, [currentPage, dispatch, devicesData]);

  const totalPages = Math.ceil(displayedItems.length / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const displayedPaginationItems = displayedItems.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    let startPage, endPage;

    if (totalPages <= 3) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (currentPage <= 2) {
        startPage = 1;
        endPage = 3;
      } else if (currentPage + 1 >= totalPages) {
        startPage = totalPages - 2;
        endPage = totalPages;
      } else {
        startPage = currentPage - 1;
        endPage = currentPage + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li key={i} className={`page-item ${i === currentPage ? "active" : ""}`}>
          <Link to="#" className="page-link" onClick={() => paginate(i)}>
            {i}
          </Link>
        </li>
      );
    }

    return pageNumbers;
  };

  const handleEdit = (row) => {
    setEditableRow(row.device_ID);
    setEditedDeviceName(row.device_name);
    setEditedDeviceOS(row.device_OS);
  };

  const handleSave = (rowId) => {
    const updatedDevice = {
      device_ID: rowId,
      device_name: editedDeviceName,
      device_OS: editedDeviceOS,
    };

    setDisplayedItems((prevItems) =>
      prevItems.map((item) =>
        item.device_ID === rowId ? { ...item, device_name: editedDeviceName, device_OS: editedDeviceOS } : item
      )
    );

    dispatch(updateDeviceRequest(updatedDevice));
    toast.success('Device Data Update')
    resetEditingState();
  };

  const handleCancel = () => {
    resetEditingState();
  };

  const resetEditingState = () => {
    setEditableRow(null);
    setEditedDeviceName("");
    setEditedDeviceOS("");
  };

  return (
    <React.Fragment>
      <Toaster />
      <Col lg={12}>
        <Card style={{ paddingBottom: "55px" }}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <h4 className="card-title">Devices</h4>
              <Form className="app-search d-none d-lg-block pt-2" onSubmit={(e) => e.preventDefault()}>
                <div className="position-relative">
                  <Input
                    type="text"
                    className="form-control"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search"
                  />
                  <span className="ri-search-line"></span>
                </div>
              </Form>
            </div>
            {loading || searchLoading ? (
              <div className="table-responsive p-2" style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50%"
              }}>
                <PageSection />
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered" style={{ minWidth: "max-content" }}>
                  <thead>
                    <tr>
                      <th className="p-3">No.</th>
                      <th className="p-3">Device ID</th>
                      <th className="p-3">Device Name</th>
                      <th className="p-3">Device OS</th>
                      <th className="p-3">Email</th>
                      <th className="p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedPaginationItems.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="text-center p-5">
                          {noResultsFound ? (
                            <div>No data found</div>
                          ) : (
                            <PageSection />
                          )}
                        </td>
                      </tr>
                    ) : (
                      displayedPaginationItems.map((row, index) => (
                        <tr key={row.device_ID} style={{ backgroundColor: editableRow === row.device_ID ? "#f5f5f5" : "white" }}>
                          <td className="p-3">{indexOfFirstItem + index + 1}</td>
                          <td className="p-3">{row.device_ID}</td>
                          <td className="p-3">
                            {editableRow === row.device_ID ? (
                              <Input
                                type="text"
                                value={editedDeviceName}
                                onChange={(e) => setEditedDeviceName(e.target.value)}
                                autoFocus
                              />
                            ) : (
                              row.device_name
                            )}
                          </td>
                          <td className="p-3">
                            {editableRow === row.device_ID ? (
                              <Input
                                type="text"
                                value={editedDeviceOS}
                                onChange={(e) => setEditedDeviceOS(e.target.value)}
                              />
                            ) : (
                              row.device_OS
                            )}
                          </td>
                          <td className="p-3">{row.user_email}</td>
                          <td className="p-3">
                            {editableRow === row.device_ID ? (
                              <>
                                <Link to="#" className="me-3 text-success" onClick={() => handleSave(row.device_ID)}>
                                  <i className="mdi mdi-check font-size-18"></i>
                                </Link>
                                <Link to="#" className="text-danger" onClick={handleCancel}>
                                  <i className="mdi mdi-close font-size-18"></i>
                                </Link>
                              </>
                            ) : (
                              <Link to="#" className="me-3 text-primary" onClick={() => handleEdit(row)}>
                                <i className="mdi mdi-pencil font-size-18"></i>
                              </Link>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>

                </table>
                {displayedItems.length > itemsPerPage && (
                  <nav>
                    <ul className="pagination justify-content-end">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <Link to="#" className="page-link" onClick={() => paginate(currentPage - 1)}>&laquo;</Link>
                      </li>
                      {renderPageNumbers()}
                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <Link to="#" className="page-link" onClick={() => paginate(currentPage + 1)}>&raquo;</Link>
                      </li>
                    </ul>
                  </nav>
                )}
              </div>
            )}
          </CardBody>

        </Card>
      </Col>
    </React.Fragment>
  );
};

export default AllDeviceInfo;
